import { useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import LookupSelect from '../components/LookupSelect'

interface Nota {
  empcod: number
  serie: string
  numero: number
  cliCod: string
  fornecedor: string
  cidade: string
  dataEmissao: string | null
  dataRecebimento: string | null
  inss: number
  irrf: number
  issqn: number
  pisCofinsCsll: number
  valorBruto: number
  valorLiquido: number
  qtdParcelas: number
  primeiroVencimento: string | null
}

function hojeISO() { return new Date().toISOString().slice(0, 10) }
function mesAtrasISO() {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return d.toISOString().slice(0, 10)
}
function fmtData(v: string | null) { return v ? v.slice(0, 10).split('-').reverse().join('/') : '-' }
function fmtMoeda(v: number) { return (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.85rem', height: 32, background: '#fff',
}
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }

function Campo({ rotulo, width, children }: { rotulo: string; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

interface TotaisImpostos { inss: number; irrf: number; issqn: number; pisCofinsCsll: number; bruto: number; liquido: number }
function somaZero(): TotaisImpostos { return { inss: 0, irrf: 0, issqn: 0, pisCofinsCsll: 0, bruto: 0, liquido: 0 } }
function acumula(t: TotaisImpostos, n: Nota) {
  t.inss += n.inss; t.irrf += n.irrf; t.issqn += n.issqn; t.pisCofinsCsll += n.pisCofinsCsll
  t.bruto += n.valorBruto; t.liquido += n.valorLiquido
}

export default function RelatorioNotasImpostoRetido() {
  const { empresaCod } = useEmpresa()
  const [empcodDe, setEmpcodDe] = useState(String(empresaCod ?? 1))
  const [empcodAte, setEmpcodAte] = useState(String(empresaCod ?? 1))
  const [dataDe, setDataDe] = useState(mesAtrasISO())
  const [dataAte, setDataAte] = useState(hojeISO())
  const [classificacaoPor, setClassificacaoPor] = useState<'recebimento' | 'emissao'>('recebimento')
  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')
  const [tipoFornecedor, setTipoFornecedor] = useState<'' | 'J' | 'F' | 'E'>('')
  const [totaisPorEmpresa, setTotaisPorEmpresa] = useState(true)
  const [totaisPorMes, setTotaisPorMes] = useState(false)
  const [detalharIrrf, setDetalharIrrf] = useState(false)
  const [irrf, setIrrf] = useState(true)
  const [issqn, setIssqn] = useState(true)
  const [inss, setInss] = useState(true)
  const [pis, setPis] = useState(true)
  const [cofins, setCofins] = useState(true)
  const [cssl, setCssl] = useState(true)

  const [carregando, setCarregando] = useState(false)
  const [gerandoPdf, setGerandoPdf] = useState(false)
  const [gerandoExcel, setGerandoExcel] = useState(false)
  const [erro, setErro] = useState('')
  const [notas, setNotas] = useState<Nota[] | null>(null)

  function filtrosParams() {
    return {
      empcodDe: empcodDe || undefined, empcodAte: empcodAte || undefined,
      dataDe: dataDe || undefined, dataAte: dataAte || undefined,
      classificacaoPor, fornecedorCod: fornecedorCod || undefined,
      tipoFornecedor: tipoFornecedor || undefined,
      irrf: irrf ? 'S' : 'N', issqn: issqn ? 'S' : 'N', inss: inss ? 'S' : 'N',
      pis: pis ? 'S' : 'N', cofins: cofins ? 'S' : 'N', cssl: cssl ? 'S' : 'N',
    }
  }

  async function gerar() {
    setCarregando(true); setErro(''); setNotas(null)
    try {
      const r = await api.get('/relatorios-compras/notas-imposto-retido', { params: filtrosParams() })
      setNotas(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function gerarPdf() {
    setGerandoPdf(true); setErro('')
    try {
      const r = await api.get('/relatorios-compras/notas-imposto-retido/pdf', {
        params: {
          ...filtrosParams(), fornecedorDes: fornecedorDes || undefined,
          totaisPorEmpresa: totaisPorEmpresa ? 'S' : 'N', totaisPorMes: totaisPorMes ? 'S' : 'N',
          detalharIrrf: detalharIrrf ? 'S' : 'N',
        },
        responseType: 'blob',
      })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
    } catch {
      setErro('Erro ao gerar PDF.')
    } finally {
      setGerandoPdf(false)
    }
  }

  async function gerarExcel() {
    setGerandoExcel(true); setErro('')
    try {
      const r = await api.get('/relatorios-compras/notas-imposto-retido/excel', { params: filtrosParams(), responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'notas-imposto-retido.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setErro('Erro ao gerar Excel.')
    } finally {
      setGerandoExcel(false)
    }
  }

  const totalGeral = (notas ?? []).reduce((t, n) => { acumula(t, n); return t }, somaZero())

  const totaisEmpresaMap = new Map<number, TotaisImpostos>()
  for (const n of notas ?? []) {
    const t = totaisEmpresaMap.get(n.empcod) ?? somaZero()
    acumula(t, n)
    totaisEmpresaMap.set(n.empcod, t)
  }

  const totaisMesMap = new Map<string, TotaisImpostos>()
  for (const n of notas ?? []) {
    const dataClassif = classificacaoPor === 'emissao' ? n.dataEmissao : n.dataRecebimento
    if (!dataClassif) continue
    const chave = `${n.empcod}|${dataClassif.slice(0, 7)}`
    const t = totaisMesMap.get(chave) ?? somaZero()
    acumula(t, n)
    totaisMesMap.set(chave, t)
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>
        Notas Fiscais C/Retenção de Impostos
      </h2>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
          <Campo rotulo="Empresa De" width={90}>
            <input style={inp} inputMode="numeric" value={empcodDe} onChange={e => setEmpcodDe(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo rotulo="Até" width={90}>
            <input style={inp} inputMode="numeric" value={empcodAte} onChange={e => setEmpcodAte(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo rotulo="Data Processamento De" width={150}>
            <input style={inp} type="date" value={dataDe} onChange={e => setDataDe(e.target.value)} />
          </Campo>
          <Campo rotulo="Até" width={150}>
            <input style={inp} type="date" value={dataAte} onChange={e => setDataAte(e.target.value)} />
          </Campo>
          <Campo rotulo="Classificar Por" width={160}>
            <select style={inp} value={classificacaoPor} onChange={e => setClassificacaoPor(e.target.value as any)}>
              <option value="recebimento">Data de Recebimento</option>
              <option value="emissao">Data de Emissão</option>
            </select>
          </Campo>
          <Campo rotulo="Fornecedor (opcional)" width={260}>
            <LookupSelect
              endpoint="/erp/clientes/lista" codKey="cli_cod" descKey="cli_fan"
              codValue={fornecedorCod} descValue={fornecedorDes}
              onSelect={(cod, des) => { setFornecedorCod(cod); setFornecedorDes(des) }}
              placeholder="Nulo p/ todos" listWidth={320} somenteComTexto
            />
          </Campo>
          <Campo rotulo="Nat. do Fornecedor" width={140}>
            <select style={inp} value={tipoFornecedor} onChange={e => setTipoFornecedor(e.target.value as any)}>
              <option value="">Todas</option>
              <option value="J">Jurídica</option>
              <option value="F">Física</option>
              <option value="E">Estrangeira</option>
            </select>
          </Campo>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Campo rotulo="Totais por Empresa" width={130}>
            <select style={inp} value={totaisPorEmpresa ? 'S' : 'N'} onChange={e => setTotaisPorEmpresa(e.target.value === 'S')}>
              <option value="S">Sim</option>
              <option value="N">Não</option>
            </select>
          </Campo>
          <Campo rotulo="Totais por Mês" width={130}>
            <select style={inp} value={totaisPorMes ? 'S' : 'N'} onChange={e => setTotaisPorMes(e.target.value === 'S')}>
              <option value="N">Não</option>
              <option value="S">Sim</option>
            </select>
          </Campo>
          <Campo rotulo="Detalhar IRRF" width={130}>
            <select style={inp} value={detalharIrrf ? 'S' : 'N'} onChange={e => setDetalharIrrf(e.target.value === 'S')}>
              <option value="N">Não</option>
              <option value="S">Sim</option>
            </select>
          </Campo>
          <Campo rotulo="Seleção dos Impostos Retidos" width={420}>
            <div style={{ display: 'flex', gap: '0.9rem', alignItems: 'center', height: 32 }}>
              {[
                { l: 'IRRF', v: irrf, set: setIrrf },
                { l: 'ISSQN', v: issqn, set: setIssqn },
                { l: 'INSS', v: inss, set: setInss },
                { l: 'PIS', v: pis, set: setPis },
                { l: 'COFINS', v: cofins, set: setCofins },
                { l: 'CSSL', v: cssl, set: setCssl },
              ].map(c => (
                <label key={c.l} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', color: '#374151' }}>
                  <input type="checkbox" checked={c.v} onChange={e => c.set(e.target.checked)} />
                  {c.l}
                </label>
              ))}
            </div>
          </Campo>
          <button className="btn btn-primary btn-sm" disabled={carregando} onClick={gerar} style={{ height: 32 }}>
            {carregando ? 'Gerando...' : 'Gerar Relatório'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}

      {notas && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: '0.9rem' }}>
            <button className="btn btn-sm" disabled={gerandoPdf} onClick={gerarPdf} style={{ height: 32, background: '#dc2626', borderColor: '#dc2626', color: '#fff' }}>
              {gerandoPdf ? 'Gerando...' : '📄 Gerar PDF'}
            </button>
            <button className="btn btn-sm" disabled={gerandoExcel} onClick={gerarExcel} style={{ height: 32, background: '#15803d', borderColor: '#15803d', color: '#fff' }}>
              {gerandoExcel ? 'Gerando...' : '📊 Gerar Excel'}
            </button>
          </div>

          {notas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhuma nota encontrada.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="table" style={{ width: '100%', fontSize: '0.78rem', marginBottom: '0.5rem' }}>
                  <thead>
                    <tr className="thead-claro" style={{ background: '#eef2f7' }}>
                      <th style={{ textAlign: 'left' }}>Fornecedor</th>
                      <th style={{ textAlign: 'left', width: 120 }}>Cidade</th>
                      <th style={{ textAlign: 'center', width: 70 }}>Nota</th>
                      <th style={{ textAlign: 'center', width: 50 }}>Sér</th>
                      <th style={{ textAlign: 'center', width: 90 }}>Emissão</th>
                      <th style={{ textAlign: 'center', width: 90 }}>Recebim.</th>
                      <th style={{ textAlign: 'right', width: 90 }}>INSS</th>
                      <th style={{ textAlign: 'right', width: 90 }}>IRRF</th>
                      <th style={{ textAlign: 'right', width: 90 }}>ISSQN</th>
                      <th style={{ textAlign: 'right', width: 100 }}>Pis+Cof+Csl</th>
                      <th style={{ textAlign: 'right', width: 100 }}>Vlr. Bruto</th>
                      <th style={{ textAlign: 'right', width: 100 }}>Vlr. Líquido</th>
                      <th style={{ textAlign: 'center', width: 120 }}>Parc./Venc. 1ª</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notas.map((n, idx) => (
                      <tr key={`${n.empcod}-${n.serie}-${n.numero}-${n.cliCod}`} style={{ background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                        <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={n.fornecedor}>{n.fornecedor}</td>
                        <td>{n.cidade || '-'}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#1a6ea8' }}>{n.numero}</td>
                        <td style={{ textAlign: 'center' }}>{n.serie}</td>
                        <td style={{ textAlign: 'center' }}>{fmtData(n.dataEmissao)}</td>
                        <td style={{ textAlign: 'center' }}>{fmtData(n.dataRecebimento)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoeda(n.inss)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoeda(n.irrf)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoeda(n.issqn)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoeda(n.pisCofinsCsll)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoeda(n.valorBruto)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoeda(n.valorLiquido)}</td>
                        <td style={{ textAlign: 'center', color: '#6b7280' }}>
                          {n.qtdParcelas || '-'} / {n.primeiroVencimento ? fmtData(n.primeiroVencimento) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 700 }}>
                      <td colSpan={6} style={{ textAlign: 'right', padding: '6px 8px' }}>TOTAL:</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px' }}>{fmtMoeda(totalGeral.inss)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px' }}>{fmtMoeda(totalGeral.irrf)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px' }}>{fmtMoeda(totalGeral.issqn)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px' }}>{fmtMoeda(totalGeral.pisCofinsCsll)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px' }}>{fmtMoeda(totalGeral.bruto)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px' }}>{fmtMoeda(totalGeral.liquido)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {detalharIrrf && (() => {
                const notasComIrrf = notas.filter(n => n.irrf !== 0)
                const detBruto = notasComIrrf.reduce((s, n) => s + n.valorBruto, 0)
                const detLiquido = notasComIrrf.reduce((s, n) => s + n.valorLiquido, 0)
                const detImposto = notasComIrrf.reduce((s, n) => s + n.irrf, 0)
                return (
                  <div style={{ marginTop: '1.25rem', border: '1px solid #111', borderRadius: 4, maxWidth: 640 }}>
                    <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.8rem', padding: '6px 8px', borderBottom: '1px solid #d1d5db' }}>
                      DETALHAMENTO DO IRRF POR CÓDIGO DE RETENÇÃO
                    </div>
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', padding: '8px 12px', fontSize: '0.78rem', fontWeight: 700 }}>
                      <span>COD. IRRF.: 0</span>
                      <span>Valor Bruto R$.: {fmtMoeda(detBruto)}</span>
                      <span>Valor Líquido R$.: {fmtMoeda(detLiquido)}</span>
                      <span>Valor Imposto R$.: {fmtMoeda(detImposto)}</span>
                    </div>
                  </div>
                )
              })()}

              {totaisPorEmpresa && (
                <div style={{ marginTop: '1.25rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
                    Total das Notas com Retenção de Impostos por Estabelecimento
                  </div>
                  <table className="table" style={{ width: '100%', fontSize: '0.78rem' }}>
                    <thead>
                      <tr className="thead-claro" style={{ background: '#eef2f7' }}>
                        <th style={{ textAlign: 'left', width: 80 }}>Emp</th>
                        <th style={{ textAlign: 'right' }}>INSS</th>
                        <th style={{ textAlign: 'right' }}>IRRF</th>
                        <th style={{ textAlign: 'right' }}>ISSQN</th>
                        <th style={{ textAlign: 'right' }}>Pis+Cofins+Csll</th>
                        <th style={{ textAlign: 'right' }}>Valor Bruto</th>
                        <th style={{ textAlign: 'right' }}>Valor Líquido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...totaisEmpresaMap.entries()].sort((a, b) => a[0] - b[0]).map(([emp, t]) => (
                        <tr key={emp}>
                          <td>{emp}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoeda(t.inss)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoeda(t.irrf)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoeda(t.issqn)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoeda(t.pisCofinsCsll)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoeda(t.bruto)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoeda(t.liquido)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 700 }}>
                        <td>TOTAL</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalGeral.inss)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalGeral.irrf)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalGeral.issqn)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalGeral.pisCofinsCsll)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalGeral.bruto)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalGeral.liquido)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {totaisPorMes && (
                <div style={{ marginTop: '1.25rem' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
                    Total das Notas com Retenção de Impostos por Mês
                  </div>
                  <table className="table" style={{ width: '100%', fontSize: '0.78rem' }}>
                    <thead>
                      <tr className="thead-claro" style={{ background: '#eef2f7' }}>
                        <th style={{ textAlign: 'left', width: 80 }}>Emp</th>
                        <th style={{ textAlign: 'left', width: 140 }}>Mês / Ano</th>
                        <th style={{ textAlign: 'right' }}>INSS</th>
                        <th style={{ textAlign: 'right' }}>IRRF</th>
                        <th style={{ textAlign: 'right' }}>ISSQN</th>
                        <th style={{ textAlign: 'right' }}>Pis+Cofins+Csll</th>
                        <th style={{ textAlign: 'right' }}>Bruto R$</th>
                        <th style={{ textAlign: 'right' }}>Líquido R$</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...totaisMesMap.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([chave, t]) => {
                        const [emp, anoMes] = chave.split('|')
                        const [ano, mes] = anoMes.split('-').map(Number)
                        return (
                          <tr key={chave}>
                            <td>{emp}</td>
                            <td>{MESES[mes - 1]} / {ano}</td>
                            <td style={{ textAlign: 'right' }}>{fmtMoeda(t.inss)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtMoeda(t.irrf)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtMoeda(t.issqn)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtMoeda(t.pisCofinsCsll)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtMoeda(t.bruto)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtMoeda(t.liquido)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 700 }}>
                        <td colSpan={2}>TOTAL...:</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalGeral.inss)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalGeral.irrf)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalGeral.issqn)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalGeral.pisCofinsCsll)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalGeral.bruto)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(totalGeral.liquido)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
